// Models
import {
  CompositeQueryTreeNode,
  ConstantValue,
  DmAggregateFunctions,
  DmFactTypes,
  DmEventSubtype,
  DmFact,
  DmObjectToFlag,
  DmOperator,
  DurationValue,
  FieldValue,
  IRSchema,
  MatchlistFieldValue,
  MetaUi,
  NullValue,
  QueryTreeNode,
  RtrTypes,
  VariableValue,
  CompositeTypes,
  ValueTypes,
  UiInputs,
  DmSenderReceivers,
  DmDurationUnits,
  TagValue,
} from 'app/modules/rulesAdvanced/models';
import {
  AndOrCondition,
  Condition,
  ConditionOperandValue,
  DmEventByObjectFactOperand,
  ScenarioDropdownBuilderFormValues,
  FECompositeQueryTreeNode,
  ScenarioAndOrCondition,
} from 'app/modules/detectionModels/components/DropdownBuilder/models';
import {
  FieldValueOperand,
  LeftOperand,
  SymbolFieldValue,
  SymbolTable,
  VariableValueOperand,
} from 'app/modules/rulesAdvanced/lib/SymbolTable';

// Constants
import {
  AndOrOperator,
  ConditionalOperators,
  EMPTY_FIELD_VALUE,
  OBJECT_TO_FLAG_TO_MODEL,
  DropdownBuilderFormFields,
  EMPTY_DROPDOWN_BUILDER_CONDITION,
  EMPTY_VARIABLE_VALUE,
  EMPTY_DROPDOWN_BUILDER_AND_OR_CONDITION_WITH_NO_CONDITIONS,
  EMPTY_SCENARIO_DROPDOWN_BUILDER_AND_OR_CONDITION_WITH_NO_CONDITIONS,
} from 'app/modules/detectionModels/constants';

// Utils
import {
  determineOperator,
  isConditionalOperator,
} from 'app/modules/detectionModels/helpers';
import { convertFactConditionToRuleCondition } from 'app/modules/detectionModels/components/scenarioWidgets/helpers';
import {
  RuleRepresentationAndOrCondition,
  RuleRepresentationCondition,
} from 'app/modules/rulesAdvanced/components/RuleRepresentationForm/models';

// NOTE: Dropdown Builder helper functions
// Extracted from helpers.tsx to avoid circular dependencies
export const generateEmptyIrRule = (object: DmObjectToFlag): IRSchema => {
  return {
    facts: [],
    rule_condition: {
      type: CompositeTypes.RULE_CONDITION_COMPOSITE,
      operands: [],
      operator: 'AND',
    },
    object,
    field_to_flag: {
      type: ValueTypes.FLAG_FIELD,
      field: `${object === 'TXN_EVENT' ? 'internal_txn_type' : 'action_type'}`,
      model: OBJECT_TO_FLAG_TO_MODEL[object],
      values: [],
    },
  };
};

export const generateEventByObjectFactOperand = (
  id: string,
  aggregateFunction: DmAggregateFunctions,
  eventSubtype: DmEventSubtype,
): DmEventByObjectFactOperand => {
  return {
    type: DmFactTypes.EVENT_BY_OBJECT_FACT,
    id,
    object: 'ENTITY',
    sender_receiver: DmSenderReceivers.SENDER,
    event_filters: {
      type: CompositeTypes.COMPOSITE,
      operands: [],
      operator: 'AND',
    },
    aggregate_function: aggregateFunction,
    aggregate_field: EMPTY_FIELD_VALUE,
    event_subtype: eventSubtype,
    window_end_offset: {
      type: ValueTypes.DURATION,
      unit: DmDurationUnits.HOUR,
      value: 0,
    },
    window_start_offset: {
      type: ValueTypes.DURATION,
      unit: DmDurationUnits.DAY,
      value: 0,
    },
  };
};

export const isAndOrOperator = (
  operator: DmOperator,
): operator is AndOrOperator => {
  return operator === AndOrOperator.AND || operator === AndOrOperator.OR;
};

export const isAndOrConditionWithOutConditionsArray = (
  condition: AndOrCondition | Condition,
): condition is AndOrCondition => {
  return (
    condition !== null &&
    !Object.hasOwn(condition, DropdownBuilderFormFields.CONDITIONS) &&
    (condition.type === CompositeTypes.RULE_CONDITION_COMPOSITE ||
      condition.type === 'COMPOSITE') &&
    isAndOrOperator(condition.operator)
  );
};

export const isAndOrCondition = (
  condition: AndOrCondition | Condition | ConditionOperandValue,
): condition is AndOrCondition => {
  return (
    condition !== null &&
    (condition.type === CompositeTypes.RULE_CONDITION_COMPOSITE ||
      condition.type === 'COMPOSITE') &&
    isAndOrOperator(condition.operator)
  );
};

export const isScenarioAndOrConditionWithOutConditionsArray = (
  condition: ScenarioAndOrCondition | Condition,
): condition is ScenarioAndOrCondition => {
  return (
    condition !== null &&
    !Object.hasOwn(condition, DropdownBuilderFormFields.CONDITIONS) &&
    (condition.type === CompositeTypes.RULE_CONDITION_COMPOSITE ||
      condition.type === 'COMPOSITE') &&
    isAndOrOperator(condition.operator)
  );
};

export const isScenarioAndOrCondition = (
  condition: ScenarioAndOrCondition | Condition | ConditionOperandValue,
): condition is ScenarioAndOrCondition => {
  return (
    condition !== null &&
    (condition.type === CompositeTypes.RULE_CONDITION_COMPOSITE ||
      condition.type === 'COMPOSITE') &&
    isAndOrOperator(condition.operator)
  );
};

export const isCondition = (
  condition: AndOrCondition | Condition,
): condition is Condition => {
  return Object.hasOwn(condition, DropdownBuilderFormFields.LEFT_OPERAND);
};

export const isRuleConditionComposite = (
  operandValue: ConditionOperandValue | CompositeQueryTreeNode | QueryTreeNode,
): operandValue is FECompositeQueryTreeNode => {
  return operandValue?.type === CompositeTypes.RULE_CONDITION_COMPOSITE;
};

export const isComposite = (
  operandValue: ConditionOperandValue | CompositeQueryTreeNode | QueryTreeNode,
): operandValue is FECompositeQueryTreeNode => {
  return operandValue?.type === 'COMPOSITE';
};

export const isFieldValue = (
  operandValue: ConditionOperandValue | QueryTreeNode,
): operandValue is FieldValue | SymbolFieldValue => {
  // NOTE: the only difference is SymbolFieldValue has a 'label' prop on it
  return operandValue?.type === ValueTypes.FIELD;
};

export const isVariableValue = (
  operandValue: ConditionOperandValue | QueryTreeNode,
): operandValue is VariableValue => {
  return operandValue?.type === ValueTypes.VARIABLE;
};

export const isTagValue = (
  operandValue:
    | QueryTreeNode
    | RuleRepresentationCondition
    | Condition
    | AndOrCondition
    | RuleRepresentationAndOrCondition,
): operandValue is TagValue => {
  return operandValue?.type === ValueTypes.TAG;
};

export const getFieldValueOperandFromSymbolTable = (
  fieldValue: QueryTreeNode | FieldValue | null,
  symbolTable: SymbolTable,
): FieldValueOperand | null => {
  let fieldSymbolKey: string = '';
  if (fieldValue?.type === ValueTypes.FIELD) {
    fieldSymbolKey = fieldValue.field;
    if (/(^name|\.name)$/g.test(fieldValue.field)) {
      if (/entity/g.test(fieldValue.field) || fieldValue.model === 'entity')
        fieldSymbolKey = fieldValue.field.replace('name', 'business_name');
      else fieldSymbolKey = fieldValue.field;
    } else if (/\.?(user|business)\./gi.test(fieldValue.field))
      fieldSymbolKey = fieldValue.field.replace(/(user|business)\./gi, '');
    else if (/external_id$/g.test(fieldValue.field)) {
      if (fieldValue.model === 'txn_instrument')
        fieldSymbolKey = fieldValue.field.replace(
          'external_id',
          'instrument_id',
        );
      else if (fieldValue.model === 'txn_event') {
        if (/instrument/g.test(fieldValue.field))
          fieldSymbolKey = fieldValue.field.replace(
            'external_id',
            'instrument_id',
          );
        else if (!/entity/g.test(fieldValue.field))
          fieldSymbolKey = fieldValue.field.replace('external_id', 'event_id');
        else fieldSymbolKey = fieldValue.field;
      } else if (fieldValue.model === 'action_event') {
        if (!/entity/g.test(fieldValue.field))
          fieldSymbolKey = fieldValue.field.replace('external_id', 'event_id');
        else fieldSymbolKey = fieldValue.field;
      } else fieldSymbolKey = fieldValue.field;
    } else if (/internal_fee$/g.test(fieldValue.field))
      fieldSymbolKey = fieldValue.field.replace('internal_fee', 'fee');
    else if (/internal_txn_type$/g.test(fieldValue.field))
      fieldSymbolKey = fieldValue.field.replace('internal_txn_type', 'type');
    else if (/internal_entity_type$/g.test(fieldValue.field))
      fieldSymbolKey = fieldValue.field.replace(
        'internal_entity_type',
        'entity_subtype',
      );
    else if (/zip_code$/g.test(fieldValue.field))
      fieldSymbolKey = fieldValue.field.replace('zip_code', 'postal_code');
    else if (/entity\.type$/g.test(fieldValue.field))
      fieldSymbolKey = fieldValue.field.replace('type', 'entity_type');
    else if (/^type$/g.test(fieldValue.field) && fieldValue.model === 'entity')
      fieldSymbolKey = 'entity_type';
    else if (/usd_conversion_notes$/g.test(fieldValue.field))
      fieldSymbolKey = fieldValue.field.replace(
        'usd_conversion_notes',
        'conversion_notes',
      );
  }

  const found = symbolTable[fieldSymbolKey];
  if (
    found?.type === ValueTypes.FIELD ||
    found?.type === ValueTypes.VARIABLE ||
    found?.type === 'fact_id'
  ) {
    return null;
  }
  return found ? found.value : null;
};

export const getVariableValueOperandFromSymbolTable = (
  variableValue: VariableValue | null,
  symbolTable: SymbolTable,
): VariableValueOperand | null => {
  if (!variableValue) return null;

  const found = symbolTable[variableValue.name];
  if (
    found?.type === ValueTypes.FIELD ||
    found?.type === ValueTypes.VARIABLE ||
    found?.type === 'fact_id'
  ) {
    return null;
  }
  return found ? found.value : null;
};

export const isVariableValueOperand = (
  operandValue: LeftOperand,
): operandValue is VariableValueOperand => {
  return operandValue?.value.type === ValueTypes.VARIABLE;
};

export const isConstantValue = (
  operandValue: ConditionOperandValue | QueryTreeNode,
): operandValue is ConstantValue => {
  return operandValue?.type === ValueTypes.CONSTANT;
};

export const isAggregateVariable = (
  operandValue: ConditionOperandValue | QueryTreeNode,
): operandValue is DmEventByObjectFactOperand => {
  return operandValue?.type === DmFactTypes.EVENT_BY_OBJECT_FACT;
};

export const isDurationValue = (
  operandValue: ConditionOperandValue | QueryTreeNode,
): operandValue is DurationValue => {
  return operandValue?.type === ValueTypes.DURATION;
};

export const isMatchlistFieldValue = (
  operandValue: ConditionOperandValue | QueryTreeNode,
): operandValue is MatchlistFieldValue => {
  return operandValue?.type === ValueTypes.MATCHLIST_FIELD;
};

export const isNullValue = (
  operandValue: ConditionOperandValue | QueryTreeNode,
): operandValue is NullValue => {
  return operandValue?.type === ValueTypes.NULL;
};

export const isConditionOperandValue = (
  operandValue: QueryTreeNode | ConditionOperandValue,
): operandValue is ConditionOperandValue => {
  return (
    isAggregateVariable(operandValue) ||
    isConstantValue(operandValue) ||
    isDurationValue(operandValue) ||
    isFieldValue(operandValue) ||
    isMatchlistFieldValue(operandValue) ||
    isNullValue(operandValue)
  );
};

export const isFEComposite = (
  operand: ConditionOperandValue,
): operand is FECompositeQueryTreeNode => {
  return operand?.type === 'COMPOSITE';
};

export const isMathOperator = (
  operator: DmOperator | ConditionalOperators,
): boolean => {
  return ['ADD', 'SUB', 'MUL', 'DIV'].includes(operator);
};

export const convertOperandToNode = (
  operand: ConditionOperandValue,
): QueryTreeNode => {
  if (isFieldValue(operand)) return operand;

  if (isAggregateVariable(operand)) {
    return {
      type: ValueTypes.VARIABLE,
      name: operand.id,
    };
  }
  if (isRuleConditionComposite(operand)) {
    const operandCopy = structuredClone(operand);

    if (isMathOperator(operandCopy.operator)) {
      // TODO: chore/sc-78358/add-support-for-math-operations-other-than
      const { 1: mathRightOperand } = operandCopy.operands;
      if (isFieldValue(mathRightOperand)) {
        operandCopy.operands[1] = mathRightOperand;
      } else if (isAggregateVariable(mathRightOperand)) {
        const name = mathRightOperand.id;
        operandCopy.operands[1] = {
          type: ValueTypes.VARIABLE,
          name,
        };
      }
      return isFEComposite(operandCopy)
        ? (operandCopy as CompositeQueryTreeNode)
        : operandCopy;
    }
  }
  return isFEComposite(operand) ? (operand as CompositeQueryTreeNode) : operand;
};

export const dropdownBuilderSpecToForm = (
  condition: CompositeQueryTreeNode,
): ScenarioDropdownBuilderFormValues => {
  const generatedCondition =
    generateScenarioDropdownBuilderFormValuesFromCompositeQueryTreeNode(
      convertFactConditionToRuleCondition(condition),
      [],
    );
  if (!isAndOrCondition(generatedCondition)) {
    throw new Error('Expected AndOr condition');
  }
  return {
    condition: generatedCondition,
  };
};

// NOTE: may deprecate in the future, if we replace all dropdown builder usage with advancedOrDropdown form
export const convertConditionsToQueryTreeNodes = (
  condition: AndOrCondition | ScenarioAndOrCondition | Condition,
  rtrType?: RtrTypes,
): CompositeQueryTreeNode => {
  if (
    isAndOrCondition(condition) ||
    isAndOrConditionWithOutConditionsArray(condition) ||
    isScenarioAndOrCondition(condition) ||
    isScenarioAndOrConditionWithOutConditionsArray(condition)
  ) {
    const metaUi: MetaUi = { ui_operator: condition.operator };
    if (rtrType) metaUi.rtr_ui_subtype = rtrType;

    return {
      type: condition.type,
      operator: condition.operator,
      meta_ui: metaUi,
      operands: condition[DropdownBuilderFormFields.CONDITIONS]
        ? condition[DropdownBuilderFormFields.CONDITIONS].map(
            (nestedCondition) => {
              if (isTagValue(nestedCondition)) return nestedCondition;
              return convertConditionsToQueryTreeNodes(nestedCondition);
            },
          )
        : [],
    };
  }
  const { type, operator, leftOperand, rightOperand, betweenOperand } =
    condition;

  const operands: QueryTreeNode[] = [leftOperand];
  if (rightOperand) operands.push(convertOperandToNode(rightOperand));

  if (betweenOperand) operands.push(convertOperandToNode(betweenOperand));

  const convertedCondition: CompositeQueryTreeNode = {
    type,
    operands,
    operator,
  };
  const operatorForBE = determineOperator(convertedCondition);

  return {
    ...convertedCondition,
    operator: operatorForBE,
    meta_ui: {
      input_choice: UiInputs.DROPDOWN,
      ui_operator: operator,
    },
  };
};

export const convertVariable = (
  factList: DmFact[],
  variableName: string,
): DmEventByObjectFactOperand | null => {
  const foundFact = factList.filter((fact) => fact.id === variableName)[0];
  if (
    foundFact?.type === DmFactTypes.EVENT_BY_OBJECT_FACT &&
    typeof foundFact.aggregate_field !== 'string' &&
    isFieldValue(foundFact.aggregate_field)
  ) {
    return {
      ...foundFact,
      aggregate_field: foundFact.aggregate_field || EMPTY_FIELD_VALUE,
    };
  }
  return null;
};

export const convertNode = (
  node: QueryTreeNode,
  facts: DmFact[],
): ConditionOperandValue => {
  let operand: ConditionOperandValue = null;
  if (isFieldValue(node)) {
    operand = node;
  } else if (isRuleConditionComposite(node)) {
    const operandCopy: FECompositeQueryTreeNode = structuredClone(node);
    if (isMathOperator(operandCopy.operator)) {
      if (operandCopy.operands[1]?.type === ValueTypes.VARIABLE) {
        operandCopy.operands[1] = convertVariable(
          facts,
          operandCopy.operands[1].name,
        );
      }
      operand = operandCopy;
    }
  } else if (node?.type === ValueTypes.VARIABLE) {
    operand = convertVariable(facts, node.name);
  } else if (isComposite(node) || isConditionOperandValue(node)) {
    operand = node;
  }
  return operand;
};

const generateConditionsFromQueryTreeNode = (
  queryTreeNode: QueryTreeNode | CompositeQueryTreeNode,
  facts?: DmFact[],
  rtrType?: RtrTypes,
): AndOrCondition | Condition => {
  if (isRuleConditionComposite(queryTreeNode)) {
    if (isAndOrOperator(queryTreeNode.operator)) {
      return {
        type: queryTreeNode.type,
        operator: queryTreeNode.operator,
        conditions: queryTreeNode.operands.map((operand) =>
          generateConditionsFromQueryTreeNode(
            operand,
            facts,
            queryTreeNode.meta_ui?.rtr_ui_subtype,
          ),
        ),
      };
    }

    const [leftOperandNode, rightOperandNode, betweenOperandNode] =
      queryTreeNode.operands;
    const rightOperand: ConditionOperandValue = convertNode(
      rightOperandNode,
      facts ?? [],
    );
    const betweenOperand: ConditionOperandValue = convertNode(
      betweenOperandNode,
      facts ?? [],
    );

    const leftOperandDefaultValue: FieldValue | VariableValue =
      rtrType === RtrTypes.VELOCITY ? EMPTY_VARIABLE_VALUE : EMPTY_FIELD_VALUE;
    const leftOperand: FieldValue | VariableValue =
      isFieldValue(leftOperandNode) || isVariableValue(leftOperandNode)
        ? leftOperandNode
        : leftOperandDefaultValue;

    const uiOperator = queryTreeNode.meta_ui?.ui_operator;
    return {
      type: queryTreeNode.type,
      operator: isConditionalOperator(uiOperator)
        ? uiOperator
        : ConditionalOperators.EQ,
      leftOperand,
      rightOperand,
      betweenOperand,
    };
  }
  return EMPTY_DROPDOWN_BUILDER_CONDITION;
};

const generateScenarioConditionsFromQueryTreeNode = (
  queryTreeNode: QueryTreeNode | CompositeQueryTreeNode,
  facts?: DmFact[],
): ScenarioAndOrCondition | Condition => {
  if (isRuleConditionComposite(queryTreeNode)) {
    if (isAndOrOperator(queryTreeNode.operator)) {
      return {
        type: queryTreeNode.type,
        operator: queryTreeNode.operator,
        conditions: queryTreeNode.operands.map((operand) =>
          generateScenarioConditionsFromQueryTreeNode(operand, facts),
        ),
      };
    }

    const [leftOperandNode, rightOperandNode, betweenOperandNode] =
      queryTreeNode.operands;
    const rightOperand: ConditionOperandValue = convertNode(
      rightOperandNode,
      facts ?? [],
    );
    const betweenOperand: ConditionOperandValue = convertNode(
      betweenOperandNode,
      facts ?? [],
    );

    const leftOperand: FieldValue = isFieldValue(leftOperandNode)
      ? leftOperandNode
      : EMPTY_FIELD_VALUE;

    const uiOperator = queryTreeNode.meta_ui?.ui_operator;
    return {
      type: queryTreeNode.type,
      operator: isConditionalOperator(uiOperator)
        ? uiOperator
        : ConditionalOperators.EQ,
      leftOperand,
      rightOperand,
      betweenOperand,
    };
  }
  return EMPTY_DROPDOWN_BUILDER_CONDITION;
};

export const generateDropdownBuilderFormValuesFromCompositeQueryTreeNode = (
  queryTreeNode: CompositeQueryTreeNode | null,
  facts?: DmFact[],
): AndOrCondition => {
  const andOrCondition = generateConditionsFromQueryTreeNode(
    queryTreeNode,
    facts,
  );
  return isAndOrCondition(andOrCondition)
    ? andOrCondition
    : EMPTY_DROPDOWN_BUILDER_AND_OR_CONDITION_WITH_NO_CONDITIONS;
};

export const generateScenarioDropdownBuilderFormValuesFromCompositeQueryTreeNode =
  (
    queryTreeNode: CompositeQueryTreeNode | null,
    facts?: DmFact[],
  ): ScenarioAndOrCondition => {
    const scenarioAndOrCondition = generateScenarioConditionsFromQueryTreeNode(
      queryTreeNode,
      facts,
    );
    return isScenarioAndOrCondition(scenarioAndOrCondition)
      ? scenarioAndOrCondition
      : EMPTY_SCENARIO_DROPDOWN_BUILDER_AND_OR_CONDITION_WITH_NO_CONDITIONS;
  };

export const resetConditionsArray = (
  condition: AndOrCondition,
): AndOrCondition => {
  return { ...condition, conditions: [] };
};
